import { EVENTS } from 'util.events'

class BlockBuyButtons extends HTMLElement {
  constructor() {
    super()

    this.handleVariantChange = this.handleVariantChange.bind(this)
  }
  connectedCallback() {
    this.abortController = new AbortController()

    // Intercept on the form itself, before Shopify's form tracking runs.
    // Listening on the outer component lets Shopify count both the form
    // submission and the successful AJAX request as separate cart additions.
    const productForm = this.querySelector(`#product-form-${this.dataset.sectionId}`)
    productForm?.addEventListener('submit', this.handleSubmit.bind(this), {
      signal: this.abortController.signal
    })

    document.addEventListener(
      `${EVENTS.variantChange}:${this.dataset.sectionId}:${this.dataset.productId}`,
      this.handleVariantChange,
      {
        signal: this.abortController.signal
      }
    )

    this.cartType = this.dataset.cartType
    this.initDeferredPaymentTerms()
  }

  disconnectedCallback() {
    this.abortController.abort()
    this.paymentTermsObserver?.disconnect()
  }

  initDeferredPaymentTerms() {
    this.paymentTermsTemplate = this.querySelector('template[data-astra-payment-terms]')
    if (!this.paymentTermsTemplate) return

    const mobile = window.matchMedia?.('(max-width: 768px)')
    if (!mobile?.matches || !('IntersectionObserver' in window) ||
        this.closest('.quick-shop-modal') || !this.closest('.page-content--product')) {
      this.hydratePaymentTerms()
      return
    }

    // Only defer a positively eligible native banner. Unknown/empty/ineligible
    // metadata keeps Shopify's immediate rendering and receives no reserved gap.
    const terms = this.paymentTermsTemplate.content.querySelector('shopify-payment-terms')
    const input = this.paymentTermsTemplate.closest('form')?.querySelector('input[name="id"]')
    const currentId = String(input?.value || '').trim() || terms?.getAttribute('variant-id')
    let eligible = false
    try {
      const meta = JSON.parse(terms?.getAttribute('shopify-meta') || '{}')
      eligible = meta.type === 'product' && meta.variants?.some((variant) =>
        String(variant.id) === currentId && variant.eligible === true && variant.available === true
      )
    } catch { /* Unknown native metadata uses the immediate fallback. */ }
    if (!eligible) {
      this.hydratePaymentTerms()
      return
    }
    this.paymentTermsTemplate.parentElement.setAttribute('data-astra-payment-terms-reserved', '')

    mobile.addEventListener?.('change', (event) => {
      if (!event.matches) this.hydratePaymentTerms()
    }, { signal: this.abortController.signal })

    try {
      this.paymentTermsObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.hydratePaymentTerms()
      }, { rootMargin: '250px 0px', threshold: 0 })
      this.paymentTermsObserver.observe(this.paymentTermsTemplate.parentElement)
    } catch {
      this.hydratePaymentTerms()
    }
  }

  hydratePaymentTerms() {
    const template = this.paymentTermsTemplate
    if (!template?.isConnected) return

    // Read the current product form, including variant changes while the banner was inert.
    // A missing/empty input must retain Shopify's server-rendered variant-id.
    const input = template.closest('form')?.querySelector('input[name="id"]')
    const variantId = String(input?.value || '').trim()
    if (variantId && /^\d+$/.test(variantId)) {
      template.content.querySelector('shopify-payment-terms')?.setAttribute('variant-id', variantId)
    }

    this.paymentTermsObserver?.disconnect()
    this.paymentTermsTemplate = null
    template.replaceWith(template.content)
  }

  handleVariantChange({ detail }) {
    const { html, variant, loading } = detail
    this.clearDispatchStatus()

    if (loading) {
      if (variant) this.updateVariantInput(variant)
      this.disableAddToCartButton()
      return
    }

    this.dataset.astraPending = 'false'
    this.enableAddToCartButton()

    if (!variant) {
      this.toggleAddButton(true, this.getLocales().unavailable)
      return
    }

    this.updateVariantInput(variant)

    if (html) {
      this.renderProductInfo(html)
    }
  }

  renderProductInfo(html) {
    const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`)

    if (addButtonUpdated) {
      this.toggleAddButton(addButtonUpdated.hasAttribute('disabled'), addButtonUpdated.textContent.trim())
      const source = html.querySelector(`block-buy-buttons[data-section-id="${this.dataset.sectionId}"][data-product-id="${this.dataset.productId}"]`)
      this.dataset.astraRetired = source?.dataset.astraRetired || 'false'
      const input = this.querySelector('input[name="id"]')
      if (input) input.disabled = addButtonUpdated.hasAttribute('disabled')
      this.renderDispatchStatus(source, addButtonUpdated)
    }
  }

  clearDispatchStatus() {
    const status = this.querySelector('[data-astra-dispatch-status]')
    if (!status) return
    status.hidden = true
    status.replaceChildren()
  }

  renderDispatchStatus(source, addButton) {
    const status = this.querySelector('[data-astra-dispatch-status]')
    const updated = source?.querySelector('[data-astra-dispatch-status]')
    const input = this.querySelector('input[name="id"]')
    const sourceInput = source?.querySelector('input[name="id"]')

    // Shopify renders stock and quantity rules for the current market/variant.
    // Never retain the previous variant's promise while loading or on a missing response.
    if (!status || !updated || updated.hidden || !updated.textContent.trim() ||
        !input?.value || input.disabled || !sourceInput || sourceInput.disabled ||
        sourceInput.value !== input.value || !addButton || addButton.hasAttribute('disabled')) return

    status.innerHTML = updated.innerHTML
    status.hidden = false
  }

  getLocales() {
    this.locales = this.locales || JSON.parse(this.querySelector('[type="application/json"]').textContent)
    return this.locales
  }

  toggleAddButton(disable = true, text) {
    const productForm = this.querySelector(`#product-form-${this.dataset.sectionId}`)

    if (!productForm) return

    const addButton = productForm.querySelector('[name="add"]')
    const addButtonText = productForm.querySelector('[name="add"] > span')

    if (!addButton) return

    if (disable) {
      addButton.setAttribute('disabled', 'disabled')
      if (text) addButtonText.textContent = text
    } else {
      addButton.removeAttribute('disabled')
      // Server-rendered text wins (12 okt: "Pre-order" for US bed variants sold from incoming stock).
      addButtonText.textContent = text ||
        (this.dataset.template !== 'preorder' ? this.getLocales().addToCart : this.getLocales().preOrder)
    }
  }

  updateVariantInput(variant) {
    const productForms = this.querySelectorAll(
      `#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}`
    )

    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]')
      input.value = variant.id
      this.hydratePaymentTerms()

      // A newly mounted native banner may not have attached its form listener yet.
      // Keep its observed variant-id in sync as well as the original change event.
      const paymentTerms = productForm.querySelector('shopify-payment-terms')
      const variantId = String(variant.id)
      if (paymentTerms && paymentTerms.getAttribute('variant-id') !== variantId) {
        paymentTerms.setAttribute('variant-id', variantId)
      }

      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  async handleSubmit(event) {
    if (this.dataset.astraPending === 'true' || this.querySelector('[name="add"]')?.disabled) {
      event.preventDefault()
      return
    }
    if (this.cartType == 'page') return

    event.preventDefault()

    const addButton = this.querySelector(`#ProductSubmitButton-${this.dataset.sectionId}`)
    if (addButton?.classList.contains('btn--loading')) return

    this.disableAddToCartButton()

    try {
      const responseJson = await this.addVariantToCart()

      this.dispatchEvent(
        new CustomEvent(EVENTS.ajaxProductAdded, {
          bubbles: true,
          detail: {
            product: responseJson,
            addToCartBtn: this.querySelector(`#ProductSubmitButton-${this.dataset.sectionId}`)
          }
        })
      )
    } catch (error) {
      this.handleError(error)
    } finally {
      this.enableAddToCartButton()
    }
  }

  handleError(error) {
    if (!error.description) {
      console.warn(error)
      return
    }

    let form = this.querySelector('form')
    let errors = this.querySelector('form .errors')

    if (errors) errors.remove()

    let errorDiv = document.createElement('div')
    errorDiv.classList.add('errors', 'text-center')

    if (typeof error.description === 'object') {
      errorDiv.textContent = error.message
    } else {
      errorDiv.textContent = error.description
    }

    form.append(errorDiv)

    this.dispatchEvent(
      new CustomEvent(EVENTS.ajaxProductError, {
        bubbles: true,
        detail: {
          errorMessage: error.description
        }
      })
    )
  }

  async addVariantToCart() {
    const formData = this.getFormDataWithSections()

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })

    if (!response.ok) {
      throw await response.json()
    }

    return response.json()
  }

  async fetchCart() {
    return (await fetch(`${window.Shopify.routes.root}cart.js`)).json()
  }

  getFormDataWithSections() {
    const productForm = this.querySelector(`#product-form-${this.dataset.sectionId}`)
    const formData = new FormData(productForm)

    formData.set('sections_url', `${window.Shopify.routes.root}variants/${productForm.id.value}`)
    // Bundled section rendering
    formData.set('sections', 'cart-ajax')

    return formData
  }

  enableAddToCartButton() {
    const productForm = this.querySelector(`#product-form-${this.dataset.sectionId}`)

    if (!productForm) return

    const addButton = productForm.querySelector('[name="add"]')
    addButton.removeAttribute('aria-busy')
    addButton.classList.remove('btn--loading')
  }

  disableAddToCartButton() {
    const productForm = this.querySelector(`#product-form-${this.dataset.sectionId}`)
    const errors = this.querySelector('form .errors')

    if (errors) errors.remove()
    if (!productForm) return

    const addButton = productForm.querySelector('[name="add"]')
    addButton.setAttribute('aria-busy', 'true')
    addButton.classList.add('btn--loading')
  }
}

customElements.define('block-buy-buttons', BlockBuyButtons)
